import type { GewerkDto } from '../models/gewerk.model';

/**
 * Bridges the backend's PascalCase enum-name ids ("Elektriker",
 * "TransportUmzug") to the snake_case keys used inside the decision-tree spec
 * ("elektriker", "transport_umzug").
 *
 * Most ids round-trip via plain lowercase; compound names need an explicit
 * override because the spec uses underscores while `nameof(Gewerk.X)` doesn't.
 * Mirrors backend `TradeMapping` so both sides agree on the wire format.
 */
const OVERRIDES: Record<string, string> = {
  TransportUmzug: 'transport_umzug',
};

export function gewerkIdToTradeKey(id: string): string {
  return OVERRIDES[id] ?? id.toLowerCase();
}

/**
 * Looks up a Gewerk from free-text input (e.g. the home search bar). Tolerant
 * of casing, German umlaut transliterations ("Sanitaer" → "Sanitär"), and
 * surrounding whitespace. Returns null if no unique match is found.
 */
export function findGewerkByText(text: string, gewerke: readonly GewerkDto[]): GewerkDto | null {
  const needle = normalize(text);
  if (!needle) return null;

  const exact = gewerke.find((g) => normalize(g.label) === needle || normalize(g.id) === needle);
  if (exact) return exact;

  const prefix = gewerke.filter((g) => normalize(g.label).startsWith(needle));
  return prefix.length === 1 ? prefix[0] : null;
}

function normalize(s: string): string {
  return s
    .trim()
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss');
}
